<script setup lang="ts">
  import { wait } from '@borg/utils';
  import { MpLoadingContainer, pushToastMessage } from '@borg/ui';
  import { JobApplicationType } from '@borg/types';

  const ALLOWED_APPLICATION_TYPES: JobApplicationType[] = ['full', 'quick'];

  const { t } = useI18n();
  const route = useRoute();
  const router = useRouter();
  const config = useRuntimeConfig();
  const projectConfig = useProjectConfig();

  const baseUrl = `https://${config.public.project.domain}`;
  const canonicalUrl = `${baseUrl}${router.currentRoute.value.path.toString()}`;

  definePageMeta({
    showFooter: false,
  });

  const tags = getJobMetaTags();
  useHead({ ...tags.head, link: [{ rel: 'canonical', href: canonicalUrl }] });
  useSeoMeta(tags.seo);

  const jobId = route.params.jobId?.toString();
  const jobApplicationSource = route.query.source?.toString();
  const job = jobRepository.use();
  const isFormDisabled = ref(false);
  const title = computed(() => `${t('JOB_APPLICATION_PAGE.TITLE')} - ${job.value?.title}`);
  const isSending = computed(() => form.completeStatus === 'inProgress');

  async function checkIfApplicationIsAllowed() {
    const supportsInternalApplication = job.value?.applicationTypes.some((applicationType) =>
      ALLOWED_APPLICATION_TYPES.includes(applicationType.type),
    );

    if (job.value && !job.value.isActive) {
      isFormDisabled.value = true;
      await pushToastMessage({
        type: 'warning',
        title: t('JOB_APPLICATION_PAGE.JOB_EXPIRED_WARNING'),
        subtitle: t('JOB_APPLICATION_PAGE.REDIRECTING_TO_JOB_PAGE_MESSAGE'),
      });
      logger.warn('Apply page opened on a job ad with isActive=false, redirecting');
      navigateToJob(job.value.id, job.value.slug, { source: 'apply_expired' }, { replace: true });
    }

    if (job.value && !supportsInternalApplication) {
      isFormDisabled.value = true;
      await pushToastMessage({
        type: 'warning',
        title: t('JOB_APPLICATION_PAGE.APPLICATION_TYPE_UNSUPPORTED_WARNING'),
        subtitle: t('JOB_APPLICATION_PAGE.REDIRECTING_TO_JOB_PAGE_MESSAGE'),
      });
      logger.warn(
        'Apply page opened on a job ad where this type of application is not supported, redirecting',
      );
      navigateToJob(
        job.value.id,
        job.value.slug,
        { source: 'apply_unsupported_application_type' },
        { replace: true },
      );
    }
  }

  useHead({
    title: title,
    meta: [{ name: 'description', content: t('JOB_APPLICATION_PAGE.DESCRIPTION') }],
  });

  useSeoMeta({
    ogTitle: title,
    ogDescription: t('JOB_APPLICATION_PAGE.DESCRIPTION'),
  });

  const { $gtag } = useNuxtApp();
  const userInfo = useUserInfo();

  const { pending, error } = await jobApplicationRepository.get(jobId, jobApplicationSource);

  const isConflictError = computed(() => {
    // TODO - do better. Previous test did not work in production
    if (projectConfig.name === 'mojposaoba') {
      // console.warn(1, error);
      // console.warn(2, error.value);
      // console.warn(3, String(error.value));
      const string = String(error.value);
      // console.warn(4, string);
      const match = string.match(/(409 Conflict)/);
      // console.warn(5, match);
      return !!match?.length;
    }
    return false;
  });

  const isGeneralError = computed(() => {
    return !!error.value && !isConflictError.value;
  });

  watch([pending, isConflictError], async (value, oldValue) => {
    const oldPending = oldValue[0];
    const newPending = value[0];
    const newError = value[1];
    if (oldPending && !newPending && newError) {
      pushToastMessage({
        type: 'alert',
        title: t('JOB_APPLICATION_CODE_VERIFICATION_PAGE.ALREADY_APPLIED.TITLE'),
        subtitle: t('JOB_APPLICATION_CODE_VERIFICATION_PAGE.ALREADY_APPLIED.SUBTITLE'),
      });
      if (job.value) {
        await navigateToJob(
          job.value.id,
          job.value.slug,
          { ...route.query, source: 'apply_page_error_already_applied' },
          { replace: true },
        );
      }
    }
  });

  // Provide apply form after job application is loaded, so that we can use the job application data
  const form = applyForm.provide();


  async function complete(code?: string) {
    await Promise.all([form.complete(code), wait(550)]);
    if (form.completeStatus === 'isSuccess') {
      $gtag?.logJob('job-apply', 'conversion');
      userInfo.setApplied(jobId);
      if (form.applicationActions?.shouldRegister) {
        await form.goTo('success-confirm-account');
      } else if (form.applicationActions?.shouldSaveSearch) {
        await form.goTo('success-save-search');
      } else {
        await form.goTo('success-recommended-jobs');
      }
    }
  }

  async function nextView(code?: string) {
    if (form.viewState === 'form') {
      if (form.hasQuestionnaire) {
        form.goTo('questionnaire');
      } else {
        form.goTo('overview');
      }
    } else if (form.viewState === 'questionnaire') {
      form.goTo('overview');
    } else if (form.viewState === 'overview') {
      if (form.needsVerification) {
        await form.startVerification();
        if (form.startVerificationStatus === 'isSuccess') {
          form.goTo('verify');
        }
      } else {
        complete();
      }
    } else if (form.viewState === 'verify' && code) {
      complete(code);
    }
  }

  function prevView() {
    if (form.viewState === 'form') {
      job.value &&
        navigateToJob(
          job.value.id,
          job.value.slug,
          { source: 'apply_page_back_button' },
          { replace: true },
        );
    } else if (form.viewState === 'questionnaire') {
      form.goTo('form');
    } else if (form.viewState === 'overview') {
      if (form.hasQuestionnaire) {
        form.goTo('questionnaire');
      } else {
        form.goTo('form');
      }
    } else if (form.viewState === 'verify') {
      form.goTo('overview');
    }
  }

  async function resendVerificationCode() {
    await form.resendVerification();
  }

  onMounted(() => {
    checkIfApplicationIsAllowed();
    $gtag?.logJob('job-apply', 'conversionintent');
  });
</script>

<template>
  <MpLoadingContainer
    :is-loading="pending"
    :has-error="!!isGeneralError"
    full-screen
  >
    <WithBackButton
      :visible="!form.viewState.includes('success')"
      @click="prevView"
    >
      <div class="apply">
        <JobCardNav v-if="!form.viewState.includes('success')" />
        <ApplyForm
          v-if="form.viewState === 'form'"
          :disabled="isFormDisabled"
          @next="nextView"
        />
        <ApplyQuestionnaire
          v-else-if="form.viewState === 'questionnaire'"
          @next="nextView"
        />
        <ApplyVerify
          v-else-if="form.viewState === 'verify'"
          @resend-verification="resendVerificationCode"
          @next="nextView"
        />
        <ApplyOverview
          v-else-if="form.viewState === 'overview'"
          @next="nextView"
        />
      </div>
    </WithBackButton>
    <ApplySuccess
      v-if="form.viewState.includes('success')"
      @next="nextView"
    />
    <ApplySending :is-sending="isSending" />
  </MpLoadingContainer>
</template>

<style scoped lang="scss">
  .apply {
    display: flex;
    flex-direction: column;
    gap: var(--mp-space-60);
    max-width: var(--site-application-form-max-width);
  }
</style>
